import { Tab } from '../homepage-tabs/TabsConfigurator'
import { TimeRanges } from '../widget-points-leaderboard/indexWidget.sync'
import { Group } from '../groups-destination/GroupOverview.sync'
import { WidgetSetting } from '../mega-menu/Types'
import { TopicBannerData } from '../topic-banner/TopicBannerWithHelp'

export interface HomepageElementInterface {
  id: number
  active: boolean
  nonConfigurable?: boolean
  title?: string
}

interface HTMLHomepageElement extends HomepageElementInterface {
  type: 'html_widget'
  title: string
  config: {
    html: string
  }
}

export const GenericHomepageElements: GenericHomepageElementTypes[] = [
  'header',
  'navigation_bar',
  'statistics_bar',
  'introduction_bar',
  'feature_topics',
  'announcement_card',
  'breadcrumb',
  'footer',
  'ideation_list',
  'ideation_statistics_bar',
  'group_overview'
]

type GenericHomepageElementTypes = 'header' | 'navigation_bar' | 'statistics_bar' | 'introduction_bar' | 'feature_topics' | 'announcement_card' | 'breadcrumb' | 'footer' | 'ideation_list' | 'ideation_statistics_bar' | 'group_overview'

interface GenericHomepageElement extends HomepageElementInterface {
  type: GenericHomepageElementTypes
  config: {}
}

export interface HeroBannerHomepageElement extends HomepageElementInterface {
  type: 'hero_banner'
  config: {
    heroImage: string
    headline: string
  }
}

export interface FeaturedTopicsElement extends HomepageElementInterface {
  type: 'featured_topics'
  title: string
  config: {
    topics: number[]|Array<{id: number, type: string}>
    firstTopicsNumber: number
    buttonLabel: string
    buttonURL: string
  }
}

export interface RecommendationsHomepageElement extends HomepageElementInterface {
  type: 'recommendations'
  title: string
  config: {
    curatedTopics: number[]
  }
}

export interface KnowledgeBaseHomepageElement extends HomepageElementInterface {
  type: 'knowledge_base'
  title: string
  config: {
    selectedTemplate: number
    displayOnlyParent: boolean
  }
}

export interface TagCloudHomepageElement extends HomepageElementInterface {
  type: 'tag_cloud'
  title: string
  config: {
    amount: number
  }
}

export interface CategoryAndContentStreamHomepageElement extends HomepageElementInterface {
  type: 'category_and_topics_stream'
  config: {
    tabs: Tab[]
  }
}

export interface QuicklinksHomepageElement extends HomepageElementInterface {
  type: 'quick_links'
  title: string
  config: {
    borderRadius: number
    enableBorder: boolean
    quickLinks: Array<{
      id: string
      title: string
      description: string
      openNewTab: boolean
      iconSrc: null | string
      imageSrc: null | string
      url: string
    }>
  }
}

export interface CategoryAndContentStreamOnlyHomepageElement extends HomepageElementInterface {
  type: 'category_and_topics_stream_only'
  config: {
    tabs: Tab[]
  }
}

export interface Container21HomepageElement extends HomepageElementInterface {
  type: 'container_2_1'
  config: {
    column_1: HomepageElement[]
    column_2: HomepageElement[]
  }
}

export interface LeaderboardPointsHomepageElement extends HomepageElementInterface {
  type: 'leaderboard_points'
  title: string
  config: {
    maxResults: number
    excludeUserRole: string[]
    timeRange: TimeRanges[]
    emptyMessage: string
    isPreviewEmptyMessage: boolean
    showLink: boolean
    linkText: string
    showLoggedInUserSection: boolean
  }
}

export interface BadgesHomepageElement extends HomepageElementInterface {
  type: 'badges'
  title: string
  config: {
    amount: number
  }
}

export interface SolvedTopicsHomepageElement extends HomepageElementInterface {
  type: 'solved_topics'
  title: string
  config: {
    amount: number
  }
}

export interface IdeationPipelineHomepageElement extends HomepageElementInterface {
  type: 'ideation_pipeline'
  title: string
  config: {
    column_1: number
    column_2: number
    column_3: number
    maxHeight: number
  }
}

export interface IdeationHeroHomepageElement extends HomepageElementInterface {
  type: 'ideation_hero'
  title: string
  config: {
    heroImage: string
    title: string
    tagline: string
  }
}

export interface GenericHeroHomepageElement extends HomepageElementInterface {
  type: 'generic_hero'
  title: string
  config: {
    heroImage: string
    title: string
    tagline: string
  }
}

export interface ProductUpdatesHeroHomepageElement extends HomepageElementInterface {
  type: 'product-updates_hero'
  title: string
  config: {
    heroImage: string
    title: string
    tagline: string
  }
}

export interface IdeationListHomepageElement extends HomepageElementInterface {
  type: 'ideation_list'
  title: string
  config: {}
}

export interface IdeationStatusFilterHomepageElement extends HomepageElementInterface {
  type: 'ideation_status_filter'
  title: string
  config: {}
}

export interface ProductAreaFilterHomepageElement extends HomepageElementInterface {
  type: 'product_area_filter'
  title: string
  config: {}
}

export interface CommunityStatisticsHomepageElement extends HomepageElementInterface {
  type: 'community_statistics'
  title: string
  config: {
    total_topics: boolean
    total_comments: boolean
    total_users: boolean
  }
}

export interface MostLikedHomepageElement extends HomepageElementInterface {
  type: 'most_liked'
  title: string
  config: {
    amount: number
    period: number
  }
}

export interface NotificationCardHomepageElement extends HomepageElementInterface {
  type: 'announcement_card'
  title: string
  config: {}
}

export interface StatisticsBarHomepageElement extends HomepageElementInterface {
  type: 'statistics_bar'
  title: string
  config: {}
}

export interface IdeationStatisticsBarHomepageElement extends HomepageElementInterface {
  type: 'ideation_statistics_bar'
  title: string
  config: {}
}

export interface IntroductionBarHomepageElement extends HomepageElementInterface {
  type: 'introduction_bar'
  title: string
  config: {}
}

export interface TopicBannerHomepageElement extends HomepageElementInterface {
  type: 'topic_banner'
  config: {
    selectedTopic: null|TopicBannerData
  }
}

export interface CommunityCategoryElement extends HomepageElementInterface {
  type: 'community_category'
  title: string
  config: {
    selectedTemplate: number
    displayOnlyParent: boolean
  }
}

export interface EventCalendarElement extends HomepageElementInterface {
  type: 'event_calendar'
  title: string
  config: {
    amount: number
  }
}

export interface GroupsElement extends HomepageElementInterface {
  type: 'groups'
  title: string
  config: {
    groups: Group[]
    buttonLabel?: string
    buttonURL?: string
  }
}

export interface HeaderNavigationHomepageElement extends HomepageElementInterface {
  type: 'header_navigation'
  title: string
  config: {
    items: WidgetSetting[]
    logo: string
  }
}

export type HomepageElement =
  HTMLHomepageElement | GenericHomepageElement | QuicklinksHomepageElement | CommunityStatisticsHomepageElement |
  KnowledgeBaseHomepageElement | RecommendationsHomepageElement | CategoryAndContentStreamHomepageElement |
  HeroBannerHomepageElement | FeaturedTopicsElement | CategoryAndContentStreamOnlyHomepageElement |
  Container21HomepageElement | TagCloudHomepageElement | BadgesHomepageElement |
  SolvedTopicsHomepageElement | MostLikedHomepageElement | LeaderboardPointsHomepageElement |
  NotificationCardHomepageElement | CommunityCategoryElement | IntroductionBarHomepageElement |
  StatisticsBarHomepageElement | EventCalendarElement | GroupsElement | IdeationPipelineHomepageElement |
  IdeationHeroHomepageElement | IdeationListHomepageElement | IdeationStatusFilterHomepageElement |
  ProductAreaFilterHomepageElement | HeaderNavigationHomepageElement | ProductUpdatesHeroHomepageElement |
  IdeationStatisticsBarHomepageElement | GenericHeroHomepageElement | TopicBannerHomepageElement
